import { useCallback, useEffect, useRef, useState } from 'react'
import type { CanvasSize, SourceLike, WorldBounds } from '../../../../lib/simulation-core'
import { useRafSimulation } from '../../../../lib/simulation-core'
import type { BraitenbergWorld, VehicleConfig, VehicleState } from '../engine/types'
import { stepWorld } from '../engine/physics'
import { renderWorld, hitTestSource, canvasToWorld } from '../components/renderer'

export interface VehicleTelemetry {
  label: string
  sensors: number[]
  leftMotor: number
  rightMotor: number
}

export interface UseBraitenbergSimOptions {
  configs: VehicleConfig[]
  initialStates: VehicleState[]
  initialSources: SourceLike[]
  bounds: WorldBounds
  trailLength?: number
  autoStart?: boolean
  telemetryVehicleIndex?: number
}

function cloneStates(states: VehicleState[]): VehicleState[] {
  return states.map((s) => ({
    position: { ...s.position },
    heading: s.heading,
    leftMotor: 0,
    rightMotor: 0,
    sensorStimuli: [],
    primaryStimulus: 0,
    trail: [],
  }))
}

function cloneSources(sources: SourceLike[]): SourceLike[] {
  return sources.map((s) => ({
    ...s,
    position: { ...s.position },
  }))
}

export function useBraitenbergSim(options: UseBraitenbergSimOptions) {
  const {
    configs,
    initialStates,
    initialSources,
    bounds,
    trailLength = 150,
    autoStart = true,
    telemetryVehicleIndex = 0,
  } = options

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const canvasSizeRef = useRef<CanvasSize>({ width: 1, height: 1, dpr: 1 })
  const [telemetry, setTelemetry] = useState<VehicleTelemetry[]>(() =>
    configs.map((config) => ({
      label: config.label,
      sensors: config.sensors.map(() => 0),
      leftMotor: 0,
      rightMotor: 0,
    })),
  )
  const lastTelemetryAtRef = useRef(0)

  const worldRef = useRef<BraitenbergWorld>({
    vehicles: cloneStates(initialStates),
    sources: cloneSources(initialSources),
    bounds,
    trailLength,
  })

  // Dragging state
  const draggingRef = useRef<number>(-1)

  const step = useCallback(
    (dt: number) => {
      stepWorld(worldRef.current, configs, dt)
    },
    [configs],
  )

  const render = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    renderWorld(ctx, worldRef.current, configs, canvasSizeRef.current)

    const now = performance.now()
    if (now - lastTelemetryAtRef.current >= 100) {
      lastTelemetryAtRef.current = now
      setTelemetry(worldRef.current.vehicles.map((vehicle, index) => ({
        label: configs[index]?.label ?? String(index + 1),
        sensors: [...vehicle.sensorStimuli],
        leftMotor: vehicle.leftMotor,
        rightMotor: vehicle.rightMotor,
      })))
    }
  }, [configs])

  const simulation = useRafSimulation({ step, render, autoStart })

  const onCanvasResize = useCallback((size: CanvasSize) => {
    canvasSizeRef.current = size
  }, [])

  const reset = useCallback(() => {
    worldRef.current.vehicles = cloneStates(initialStates)
    worldRef.current.sources = cloneSources(initialSources)
    setTelemetry(configs.map((config) => ({
      label: config.label,
      sensors: config.sensors.map(() => 0),
      leftMotor: 0,
      rightMotor: 0,
    })))
  }, [configs, initialStates, initialSources])

  // Pointer events for source dragging
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const getCanvasCoords = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      const dpr = canvasSizeRef.current.dpr
      return {
        x: (e.clientX - rect.left) * dpr,
        y: (e.clientY - rect.top) * dpr,
      }
    }

    const onPointerDown = (e: PointerEvent) => {
      const coords = getCanvasCoords(e)
      const idx = hitTestSource(
        coords.x,
        coords.y,
        worldRef.current,
        canvasSizeRef.current,
      )
      if (idx >= 0) {
        draggingRef.current = idx
        canvas.setPointerCapture(e.pointerId)
        canvas.style.cursor = 'grabbing'
      }
    }

    const onPointerMove = (e: PointerEvent) => {
      const coords = getCanvasCoords(e)
      if (draggingRef.current >= 0) {
        const worldPos = canvasToWorld(
          coords.x,
          coords.y,
          worldRef.current.bounds,
          canvasSizeRef.current,
        )
        worldRef.current.sources[draggingRef.current].position = worldPos
      } else {
        const idx = hitTestSource(
          coords.x,
          coords.y,
          worldRef.current,
          canvasSizeRef.current,
        )
        canvas.style.cursor = idx >= 0 ? 'grab' : 'default'
      }
    }

    const onPointerUp = () => {
      draggingRef.current = -1
      canvas.style.cursor = 'default'
    }

    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerup', onPointerUp)
    canvas.addEventListener('pointercancel', onPointerUp)

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerup', onPointerUp)
      canvas.removeEventListener('pointercancel', onPointerUp)
    }
  }, [])

  return {
    canvasRef,
    simulation,
    onCanvasResize,
    reset,
    worldRef,
    telemetry,
  }
}
